import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import L from 'leaflet';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Upload, FileSpreadsheet, PenTool, Image as ImageIcon, Trash2, Crosshair, ArrowUp } from 'lucide';
import { lucideIcon } from '../icon.js';
import {
  COORDINATE_SYSTEMS, DEFAULT_COORDINATE_SYSTEM, PLANNED_POINT_KINDS, pointToWgs84, normalizePointName,
  guessCoordinateSystem, guessKindFromName,
} from '@mv/models';
import { api } from '../api.js';
import {
  parseCsv, decodeText, readXlsx, guessColumns, rowsToRawPoints, rawPointsToWgs84, guessSystemFor,
} from '../utils/kaart-import/table.js';
import { parseDxf, flattenDxf, boreholeCandidates, dxfToGeoJSON, guessDxfScale, dxfLayerSummary } from '../utils/kaart-import/dxf.js';
import {
  parseGeoJSON, parseKml, parseKmz, parseShapefileZip, parseShapefileFiles, guessSystemForFc, reprojectGeoJSON,
  pointFeatures, nonPointFeatures,
} from '../utils/kaart-import/gis.js';
import { fitTransform, residuals, cornersWgs84, wgs84ToSystem, renderPdfPage, loadImage } from '../utils/kaart-import/georef.js';
import { baseTiles, RotatedImageOverlay, vectorLayer, plannedPointIcon } from '../utils/kaart-import/leaflet-layers.js';

const NL_CENTER = [52.1, 5.3];
const MAX_GEOJSON_BYTES = 14 * 1024 * 1024;

const extOf = (name) => (String(name).toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';

/** Welk soort bestand is dit? Bepaalt welke stap de dialoog toont. */
function fileKind(files) {
  const exts = files.map(f => extOf(f.name));
  if (exts.includes('shp')) return 'shapefile';
  const e = exts[0];
  if (['csv', 'txt', 'tsv'].includes(e)) return 'csv';
  if (['xlsx', 'xlsm'].includes(e)) return 'xlsx';
  if (e === 'xls') return 'xls';
  if (e === 'dxf') return 'dxf';
  if (e === 'dwg') return 'dwg';
  if (['geojson', 'json'].includes(e)) return 'geojson';
  if (e === 'kml') return 'kml';
  if (e === 'kmz') return 'kmz';
  if (e === 'zip') return 'shapezip';
  if (e === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'webp', 'tif', 'tiff', 'gif', 'bmp'].includes(e)) return 'image';
  return null;
}

/**
 * map-import-dialog — een meetstaat of tekening op de projectkaart zetten.
 *
 * Drie routes, afhankelijk van het bestand:
 *
 *   TABEL (CSV, Excel)  — kolommen en stelsel worden geraden; de gebruiker ziet de punten op de
 *                         kaart en corrigeert waar nodig. Resultaat: geplande punten.
 *   VECTOR (DXF, GeoJSON, KML, shapefile) — de tekening wordt een achtergrondlaag, en een
 *                         groep punten daaruit (een blok, een laag) kan de geplande punten worden.
 *   AFBEELDING (PDF, PNG, JPG) — uitlijnen met paspunten: klik een punt op de tekening en
 *                         hetzelfde punt op de kaart (of typ de RD-coördinaat die erop staat).
 *
 * Niets wordt opgeslagen voordat de gebruiker het op de kaart heeft gezien. Een verkeerd
 * geraden stelsel of verwisselde kolommen zie je zo meteen: dan liggen de punten in Afrika.
 *
 * @fires imported  detail:{ points?: number, layer?: object }
 * @fires close
 */
export class MapImportDialog extends LitElement {
  static properties = {
    open: { type: Boolean },
    projectId: { type: String },
    coordSystem: { type: String },
    /** Bestaande geplande punten, om 'nieuw' en 'bijwerken' te tonen. */
    existingPoints: { type: Array },
    /** Bestaande boringen [{lat,lng,name}], als oriëntatie bij het uitlijnen. */
    borings: { type: Array },
    /** Vooronderzoek beschikbaar: dan verwijst de voet naar 'Importeren' voor boorpunten. */
    plannerAvailable: { type: Boolean },
    plannedPointsAllowed: { type: Boolean }, // geplande boringen zijn GeoDesk; zonder alleen tekeningen
    _over: { state: true },
    _kind: { state: true },
    _files: { state: true },
    _error: { state: true },
    _busy: { state: true },
    _base: { state: true },
    // tabel
    _sheets: { state: true },
    _sheetIdx: { state: true },
    _headerRow: { state: true },
    _mapping: { state: true },
    // stelsel (tabel + vector)
    _system: { state: true },
    _swapped: { state: true },
    _systemGuessed: { state: true },
    _mode: { state: true },
    // vector
    _fc: { state: true },
    _dxf: { state: true },
    _dxfFlat: { state: true },
    _dxfScale: { state: true },
    _candidates: { state: true },
    _candIdx: { state: true },
    _saveDrawing: { state: true },
    _layerName: { state: true },
    _defaultKind: { state: true },
    // afbeelding
    _img: { state: true },
    _cps: { state: true },
    _pending: { state: true },
    _opacity: { state: true },
  };

  constructor() {
    super();
    this.open = false;
    this.projectId = null;
    this.coordSystem = DEFAULT_COORDINATE_SYSTEM;
    this.existingPoints = [];
    this.borings = [];
    this._resetAll();
  }

  _resetAll() {
    this._destroyMaps();
    if (this._img?.url) URL.revokeObjectURL(this._img.url);
    this._kind = null;
    this._editLayer = null;
    this._files = [];
    this._error = null;
    this._busy = false;
    this._base = 'map';
    this._sheets = [];
    this._sheetIdx = 0;
    this._headerRow = 0;
    this._mapping = null;
    this._system = 'RD';
    this._swapped = false;
    this._systemGuessed = false;
    this._mode = 'merge';
    this._fc = null;
    this._dxf = null;
    this._dxfFlat = null;
    this._dxfScale = 1;
    this._candidates = [];
    this._candIdx = -1;
    this._saveDrawing = true;
    this._layerName = '';
    this._defaultKind = null;
    this._img = null;
    this._cps = [];
    this._pending = null;
    this._opacity = 0.75;
  }

  // ── Levenscyclus en kaarten ────────────────────────────────────────────────

  updated(changed) {
    if (changed.has('open') && !this.open) this._resetAll();
    if (!this.open) return;
    const step = this._step;
    const mapEl = this.renderRoot.querySelector('#preview');
    if (mapEl && !this._map) this._initMap(mapEl);
    if (!mapEl && this._map) this._destroyMaps();
    const imgEl = this.renderRoot.querySelector('#imgview');
    if (imgEl && !this._imgMap) this._initImageMap(imgEl);
    if (step === 'image') this._syncImageLayers();
    else if (this._map) this._syncPreview();
  }

  disconnectedCallback() {
    this._destroyMaps();
    super.disconnectedCallback();
  }

  _destroyMaps() {
    if (this._map) { this._map.remove(); this._map = null; }
    if (this._imgMap) { this._imgMap.remove(); this._imgMap = null; }
    this._previewLayer = null;
    this._overlay = null;
    this._cpLayer = null;
    this._imgCpLayer = null;
    this._previewSig = null;
  }

  get _step() {
    if (!this._kind) return 'pick';
    if (this._kind === 'csv' || this._kind === 'xlsx') return 'table';
    if (this._kind === 'pdf' || this._kind === 'image') return 'image';
    return 'vector';
  }

  _initMap(el) {
    this._map = L.map(el, { center: NL_CENTER, zoom: 7, maxZoom: 22 });
    this._tiles = baseTiles();
    for (const l of this._tiles[this._base]) l.addTo(this._map);
    this._map.attributionControl.setPrefix(false);
    L.control.scale({ imperial: false }).addTo(this._map);
    // Oriëntatie: bestaande boringen als kleine grijze stipjes.
    const ctx = L.layerGroup().addTo(this._map);
    for (const b of this.borings || []) {
      if (b.lat == null || b.lng == null) continue;
      L.circleMarker([b.lat, b.lng], { radius: 4, color: '#1f3a5f', weight: 1, fillOpacity: 0.7 })
        .bindTooltip(String(b.name || ''), { direction: 'top' }).addTo(ctx);
    }
    this._ctxLayer = ctx;
    if (this._step === 'image') {
      this._map.on('click', (e) => this._onMapClick(e));
      const pts = [
        ...(this.borings || []).filter(b => b.lat != null).map(b => [b.lat, b.lng]),
        ...(this.existingPoints || []).map(p => [p.y, p.x]),
      ];
      if (pts.length) this._map.fitBounds(pts, { padding: [40, 40], maxZoom: 18 });
    }
    setTimeout(() => this._map?.invalidateSize(), 50);
  }

  _setBase(kind) {
    if (kind === this._base || !this._map) { this._base = kind; return; }
    for (const l of this._tiles[this._base]) this._map.removeLayer(l);
    for (const l of this._tiles[kind]) l.addTo(this._map);
    this._base = kind;
  }

  /** Voorbeeld op de kaart opnieuw tekenen, alleen als de punten of de tekening veranderd zijn. */
  _syncPreview() {
    const pts = this._previewPoints();
    const drawing = this._step === 'vector' && this._saveDrawing ? this._previewDrawing() : null;
    const sig = JSON.stringify([pts.map(p => [p.name, p.x, p.y, p.kind]), drawing ? drawing.features.length : 0, this._system, this._swapped, this._dxfScale]);
    if (sig === this._previewSig) return;
    this._previewSig = sig;
    if (this._previewLayer) this._map.removeLayer(this._previewLayer);
    const group = L.featureGroup();
    if (drawing?.features.length) group.addLayer(vectorLayer(drawing));
    for (const p of pts) {
      if (p.error) continue;
      L.marker([p.y, p.x], { icon: plannedPointIcon(p), interactive: true })
        .bindTooltip(`${p.name}${p.plannedDepthCm ? ` · ${p.plannedDepthCm} cm` : ''}`, { direction: 'top', offset: [0, -8] })
        .addTo(group);
    }
    group.addTo(this._map);
    this._previewLayer = group;
    const b = group.getBounds();
    if (b.isValid()) this._map.fitBounds(b, { padding: [40, 40], maxZoom: 19 });
  }

  // ── Bestand kiezen en inlezen ──────────────────────────────────────────────

  async _onFiles(fileList) {
    const files = [...(fileList || [])];
    if (!files.length) return;
    const kind = fileKind(files);
    this._error = null;
    if (!kind) { this._error = 'Dit bestandstype wordt niet herkend.'; return; }
    if (kind === 'xls') { this._error = 'Oud Excel-formaat (.xls). Sla het bestand op als .xlsx of als CSV en probeer opnieuw.'; return; }
    this._busy = true;
    try {
      this._files = files;
      this._layerName = files[0].name.replace(/\.[^.]+$/, '');
      if (kind === 'csv') this._loadTable([{ name: files[0].name, rows: parseCsv(decodeText(new Uint8Array(await files[0].arrayBuffer()))).rows }]);
      else if (kind === 'xlsx') this._loadTable(readXlsx(new Uint8Array(await files[0].arrayBuffer())));
      else if (kind === 'dxf') this._loadDxf(decodeText(new Uint8Array(await files[0].arrayBuffer())));
      // DWG is een gesloten binair formaat; de backend leest hem (LibreDWG, aparte Worker) en
      // geeft hetzelfde model terug als parseDxf, zodat alles hierna gelijk loopt met DXF.
      else if (kind === 'dwg') this._loadDxf(await api.convertDwg(this.projectId, files[0]));
      else if (kind === 'geojson') this._loadFc(parseGeoJSON(await files[0].text()));
      else if (kind === 'kml') this._loadFc(parseKml(await files[0].text()));
      else if (kind === 'kmz') this._loadFc(parseKmz(new Uint8Array(await files[0].arrayBuffer())));
      else if (kind === 'shapezip') this._loadFc(parseShapefileZip(new Uint8Array(await files[0].arrayBuffer())));
      else if (kind === 'shapefile') {
        const map = {};
        for (const f of files) map[f.name] = new Uint8Array(await f.arrayBuffer());
        this._loadFc(parseShapefileFiles(map));
      } else if (kind === 'pdf') {
        const r = await renderPdfPage(await files[0].arrayBuffer());
        this._img = { ...r, url: URL.createObjectURL(r.blob) };
      } else if (kind === 'image') {
        const r = await loadImage(files[0]);
        this._img = { ...r, url: URL.createObjectURL(r.blob) };
      }
      this._kind = kind;
    } catch (e) {
      console.error('[map-import]', e);
      this._error = `Bestand kon niet gelezen worden: ${e.message || e}`;
      this._kind = null;
    } finally {
      this._busy = false;
    }
  }

  _applyGuess(guess) {
    if (guess) {
      this._system = guess.system;
      this._swapped = guess.swapped;
      this._systemGuessed = true;
    } else {
      this._system = this.coordSystem && this.coordSystem !== 'WGS84' ? this.coordSystem : 'RD';
      this._swapped = false;
      this._systemGuessed = false;
    }
  }

  _loadTable(sheets) {
    // Het eerste blad met herkenbare coördinaten; anders het eerste.
    const scored = sheets.map((s, i) => {
      const g = guessColumns(s.rows);
      return { i, g, n: g.mapping.x >= 0 ? rowsToRawPoints(s.rows, g.headerRow, g.mapping).length : 0 };
    });
    const best = scored.reduce((a, b) => (b.n > a.n ? b : a), scored[0]);
    this._sheets = sheets;
    this._selectSheet(best?.i ?? 0, best?.g);
  }

  _selectSheet(i, guess) {
    this._sheetIdx = i;
    const rows = this._sheets[i]?.rows || [];
    const g = guess || guessColumns(rows);
    this._headerRow = g.headerRow;
    this._mapping = g.mapping;
    this._applyGuess(this._mapping.x >= 0 ? guessSystemFor(rowsToRawPoints(rows, g.headerRow, g.mapping)) : null);
  }

  /** @param {string|object} input  DXF-tekst, of het al gelezen model (uit een DWG) */
  _loadDxf(input) {
    const dxf = typeof input === 'string' ? parseDxf(input) : input;
    const flat = flattenDxf(dxf);
    const sample = [...flat.lines.flatMap(l => l.coords), ...flat.points.map(p => [p.x, p.y])];
    const step = Math.max(1, Math.floor(sample.length / 400));
    const thinned = sample.filter((_, k) => k % step === 0);
    this._dxf = dxf;
    this._dxfFlat = flat;
    this._dxfScale = guessDxfScale(dxf, thinned, guessCoordinateSystem);
    this._candidates = boreholeCandidates(flat, { maxTextDistance: 5 / this._dxfScale });
    // Een groep waarvan (bijna) alle punten een naam hebben is vrijwel zeker de boorpunten.
    this._candIdx = this._candidates.length && this._candidates[0].named / this._candidates[0].count >= 0.8 ? 0 : -1;
    this._applyGuess(guessCoordinateSystem(thinned.map(([x, y]) => [x * this._dxfScale, y * this._dxfScale])));
    if (!flat.lines.length && !flat.points.length) throw new Error('geen lijnen of punten gevonden in de tekening');
  }

  _loadFc({ fc, system }) {
    this._fc = fc;
    const pts = pointFeatures(fc);
    this._candidates = pts.length ? [{ source: 'Puntobjecten uit het bestand', count: pts.length, named: pts.filter(p => p.name).length, points: pts }] : [];
    this._candIdx = this._candidates.length ? 0 : -1;
    // Alleen punten en geen lijnen: dan is er geen tekening om te bewaren.
    this._saveDrawing = nonPointFeatures(fc).features.length > 0;
    if (system) { this._system = system; this._swapped = false; this._systemGuessed = true; }
    else this._applyGuess(guessSystemForFc(fc));
  }

  // ── Afgeleide punten en tekening ───────────────────────────────────────────

  /** Punten zoals ze opgeslagen zouden worden (WGS84), met per punt 'nieuw'/'bijwerken'/fout. */
  _previewPoints() {
    let pts = [];
    if (this._step === 'table' && this._mapping && this._mapping.x >= 0 && this._mapping.y >= 0) {
      const rows = this._sheets[this._sheetIdx]?.rows || [];
      pts = rawPointsToWgs84(rowsToRawPoints(rows, this._headerRow, this._mapping), this._system, this._swapped);
    } else if (this._step === 'vector' && this._candIdx >= 0) {
      const cand = this._candidates[this._candIdx];
      const s = this._dxf ? this._dxfScale : 1;
      pts = cand.points.map((p, k) => {
        const name = p.name || `P${k + 1}`;
        const w = pointToWgs84(p.x * s, p.y * s, this._system, this._swapped);
        const kind = this._defaultKind || PLANNED_POINT_KINDS.find(d => d.id === (p.props?.soort || p.props?.type))?.id;
        return w
          ? { name, x: w.x, y: w.y, kind: kind || undefined, plannedDepthCm: null, note: null }
          : { name, error: 'niet om te rekenen' };
      });
      // Soort uit de naam als er niets gekozen is (Pb01 → peilbuis).
      pts = pts.map(p => ({ ...p, kind: p.kind || guessKindFromName(p.name) }));
    }
    const existing = new Set((this.existingPoints || []).map(p => normalizePointName(p.name)));
    const seen = new Set();
    return pts.map(p => {
      const key = normalizePointName(p.name);
      const dup = seen.has(key);
      seen.add(key);
      const outside = !p.error && (p.x < 2.5 || p.x > 7.6 || p.y < 50.5 || p.y > 53.8);
      return {
        ...p,
        error: p.error || (dup ? 'dubbele naam' : null),
        warning: outside ? 'buiten Nederland' : null,
        state: existing.has(key) && this._mode === 'merge' ? 'update' : 'new',
      };
    });
  }

  _toWgs84Fn() {
    const s = this._dxf ? this._dxfScale : 1;
    return ([x, y]) => {
      const w = pointToWgs84(x * s, y * s, this._system, this._swapped);
      return w ? [Math.round(w.x * 1e7) / 1e7, Math.round(w.y * 1e7) / 1e7] : null;
    };
  }

  /** De achtergrondtekening als GeoJSON in WGS84 (zonder de punten die geplande punten worden). */
  _previewDrawing() {
    if (this._dxfFlat) return dxfToGeoJSON(this._dxfFlat, this._toWgs84Fn());
    if (this._fc) {
      try { return reprojectGeoJSON(nonPointFeatures(this._fc), this._system, this._swapped); } catch (_) { return null; }
    }
    return null;
  }

  // ── Afbeelding uitlijnen ───────────────────────────────────────────────────

  get _georefSystem() {
    return this.coordSystem && this.coordSystem !== 'WGS84' ? this.coordSystem : 'RD';
  }

  _initImageMap(el) {
    const { width: w, height: h, url } = this._img;
    this._imgMap = L.map(el, { crs: L.CRS.Simple, minZoom: -6, maxZoom: 4, zoomSnap: 0.25, attributionControl: false });
    const bounds = [[0, 0], [h, w]];
    L.imageOverlay(url, bounds).addTo(this._imgMap);
    this._imgMap.fitBounds(bounds);
    this._imgMap.on('click', (e) => {
      const u = e.latlng.lng, v = h - e.latlng.lat;
      if (u < 0 || v < 0 || u > w || v > h) return;
      this._pending = { u, v };
    });
    setTimeout(() => this._imgMap?.invalidateSize(), 50);
  }

  _onMapClick(e) {
    if (this._step !== 'image') return;
    const p = wgs84ToSystem(e.latlng.lng, e.latlng.lat, this._georefSystem);
    if (!p) return;
    if (this._pending) {
      this._cps = [...this._cps, { ...this._pending, X: p.X, Y: p.Y }];
      this._pending = null;
    }
  }

  /** Paspunt met getypte coördinaat (staat vaak als RD-kruis of meetstaat op de tekening). */
  _addTypedCp(xText, yText) {
    // Niet X en Y noemen: X is ook het sluiticoon uit lucide.
    const oost = parseFloat(String(xText).replace(',', '.')), noord = parseFloat(String(yText).replace(',', '.'));
    if (!this._pending || !Number.isFinite(oost) || !Number.isFinite(noord)) return;
    this._cps = [...this._cps, { ...this._pending, X: oost, Y: noord }];
    this._pending = null;
  }

  _transform() { return this._cps.length >= 2 ? fitTransform(this._cps) : null; }

  _corners() {
    const t = this._transform();
    return t ? cornersWgs84(t, this._img.width, this._img.height, this._georefSystem) : null;
  }

  _syncImageLayers() {
    if (!this._map || !this._imgMap) return;
    const h = this._img.height;
    // Paspunten op de tekening
    if (this._imgCpLayer) this._imgMap.removeLayer(this._imgCpLayer);
    this._imgCpLayer = L.layerGroup().addTo(this._imgMap);
    const num = (n, color) => L.divIcon({
      className: 'mv-cp', iconSize: [22, 22], iconAnchor: [11, 11],
      html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};color:#fff;font:700 11px/22px system-ui;text-align:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${n}</div>`,
    });
    this._cps.forEach((c, k) => L.marker([h - c.v, c.u], { icon: num(k + 1, '#2e9e5b') }).addTo(this._imgCpLayer));
    if (this._pending) L.marker([h - this._pending.v, this._pending.u], { icon: num(this._cps.length + 1, '#ea580c') }).addTo(this._imgCpLayer);

    // Paspunten op de kaart
    if (this._cpLayer) this._map.removeLayer(this._cpLayer);
    this._cpLayer = L.layerGroup().addTo(this._map);
    this._cps.forEach((c, k) => {
      const sys = COORDINATE_SYSTEMS[this._georefSystem];
      const w = pointToWgs84(c.X, c.Y, sys.id);
      if (w) L.marker([w.y, w.x], { icon: num(k + 1, '#2e9e5b') }).addTo(this._cpLayer);
    });

    // De uitgelijnde tekening zelf
    const corners = this._corners();
    if (corners) {
      if (!this._overlay) {
        this._overlay = new RotatedImageOverlay(this._img.url, corners, { opacity: this._opacity }).addTo(this._map);
        this._map.fitBounds(this._overlay.getBounds(), { padding: [20, 20] });
      } else {
        this._overlay.setCorners(corners);
        this._overlay.setOpacity(this._opacity);
      }
    } else if (this._overlay) {
      this._map.removeLayer(this._overlay);
      this._overlay = null;
    }
    this._map.getContainer().style.cursor = this._pending ? 'crosshair' : '';
  }

  // ── Opslaan ────────────────────────────────────────────────────────────────

  async _save() {
    this._error = null;
    this._busy = true;
    try {
      const source = this._files[0] && this._files.length === 1 ? this._files[0] : null;
      let layer = null;
      let pointsResult = null;
      if (this._step === 'image') {
        const corners = this._corners();
        if (!corners) throw new Error('Leg minstens twee paspunten vast.');
        const georef = { ...corners, controlPoints: this._cps, opacity: this._opacity, system: this._georefSystem };
        if (this._editLayer) {
          await api.updateMapLayer(this._editLayer.id, { georef });
          layer = { ...this._editLayer, georef };
        } else {
          layer = await api.createMapLayer(this.projectId, {
            meta: { name: this._layerName || 'Tekening', kind: 'image', georef },
            data: this._img.blob,
            source,
          });
        }
      } else {
        const pts = this._previewPoints();
        const valid = pts.filter(p => !p.error);
        if (this._step === 'vector' && this._saveDrawing) {
          const gj = this._previewDrawing();
          if (gj?.features.length) {
            const text = JSON.stringify(gj);
            if (text.length > MAX_GEOJSON_BYTES) throw new Error('De tekening is te groot (meer dan 14 MB na omzetten). Verwijder in CAD overbodige lagen, of exporteer als PDF.');
            layer = await api.createMapLayer(this.projectId, { meta: { name: this._layerName || 'Tekening', kind: 'vector' }, data: text, source });
          }
        }
        if (valid.length && this.plannedPointsAllowed === false) {
          if (!layer) throw new Error('Geplande boringen importeren hoort bij GeoDesk. Dit bedrijf kan alleen tekeningen op de kaart zetten.');
        } else if (valid.length) {
          pointsResult = await api.importPlannedPoints(this.projectId,
            valid.map(({ name, kind, x, y, plannedDepthCm, note }) => ({ name, kind, x, y, plannedDepthCm, note })),
            { mode: this._mode, sourceLayerId: layer?.id ?? null, sourceName: this._files[0]?.name ?? null });
        }
        if (!layer && !pointsResult) throw new Error('Er is niets om op te slaan.');
      }
      this.dispatchEvent(new CustomEvent('imported', {
        detail: { layer, points: pointsResult?.points, added: pointsResult?.added ?? 0, updated: pointsResult?.updated ?? 0 },
        bubbles: true, composed: true,
      }));
      this._close();
    } catch (e) {
      this._error = e.message || String(e);
    } finally {
      this._busy = false;
    }
  }

  /**
   * Een bestaande PDF/afbeelding opnieuw uitlijnen: de opgeslagen afbeelding en paspunten terug in
   * de uitlijnstap. Bewaren werkt alleen de uitlijning van die tekening bij (geen nieuwe laag).
   */
  async realign(layer) {
    this._error = null;
    this._busy = true;
    try {
      const blob = await api.fetchMapLayerBlob(layer.id);
      const r = await loadImage(blob);
      this._img = { ...r, url: URL.createObjectURL(r.blob) };
      this._cps = Array.isArray(layer.georef?.controlPoints) ? layer.georef.controlPoints : [];
      this._opacity = layer.georef?.opacity ?? 0.75;
      this._layerName = layer.name;
      this._editLayer = layer;
      this._kind = 'image';
    } catch (e) {
      this._error = `Tekening kon niet geopend worden: ${e.message || e}`;
    } finally {
      this._busy = false;
    }
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  // ── Weergave ───────────────────────────────────────────────────────────────

  _systemPicker() {
    return html`
      <label class="fld">
        <span>Coördinaatsysteem ${this._systemGuessed ? html`<em>· herkend</em>` : html`<em class="warn">· niet herkend, kies zelf</em>`}</span>
        <select @change=${e => { this._system = e.target.value; this._systemGuessed = true; }}>
          ${Object.values(COORDINATE_SYSTEMS).map(s => html`<option value=${s.id} ?selected=${s.id === this._system}>${s.label}</option>`)}
        </select>
      </label>
      <label class="check">
        <input type="checkbox" .checked=${this._swapped} @change=${e => { this._swapped = e.target.checked; }}>
        X en Y verwisseld in het bestand
      </label>`;
  }

  _modePicker(points) {
    const updates = points.filter(p => p.state === 'update' && !p.error).length;
    if (!(this.existingPoints || []).length) return nothing;
    return html`
      <div class="fld">
        <span>Er staan al ${this.existingPoints.length} geplande punten</span>
        <label class="check"><input type="radio" name="mode" .checked=${this._mode === 'merge'} @change=${() => { this._mode = 'merge'; }}>
          Bijwerken en aanvullen ${updates ? html`<em>(${updates} met dezelfde naam worden verplaatst)</em>` : ''}</label>
        <label class="check"><input type="radio" name="mode" .checked=${this._mode === 'replace'} @change=${() => { this._mode = 'replace'; }}>
          Alles vervangen door dit bestand</label>
      </div>`;
  }

  _pointList(points) {
    const errors = points.filter(p => p.error).length;
    const outside = points.filter(p => p.warning).length;
    return html`
      <div class="sum">
        <b>${points.length - errors}</b> punten
        ${errors ? html` · <span class="bad">${errors} overgeslagen</span>` : ''}
        ${outside ? html` · <span class="bad">${outside} buiten Nederland — klopt het stelsel?</span>` : ''}
      </div>
      <div class="plist">
        ${points.slice(0, 300).map(p => html`
          <div class="prow ${p.error ? 'err-row' : ''}">
            <span class="pname">${p.name}</span>
            <span class="pkind">${PLANNED_POINT_KINDS.find(k => k.id === p.kind)?.label || ''}</span>
            <span class="pdepth">${p.plannedDepthCm ? `${p.plannedDepthCm} cm` : ''}</span>
            <span class="pstate">${p.error || p.warning || (p.state === 'update' ? 'bijwerken' : 'nieuw')}</span>
          </div>`)}
        ${points.length > 300 ? html`<div class="prow"><em>… en nog ${points.length - 300}</em></div>` : ''}
      </div>`;
  }

  /**
   * De eerste stap: een tekening over de kaart leggen. Een meetstaat (CSV/Excel) slepen kan nog
   * steeds en gaat naar de tabelstap, maar boorpunten en deellocaties horen bij 'Importeren' in
   * Vooronderzoek; daar verwijst de voet naar.
   */
  _renderPick() {
    return html`
      <div class="body pick">
        <label class="drop ${this._over ? 'over' : ''}"
          @dragenter=${e => { e.preventDefault(); this._over = true; }}
          @dragover=${e => { e.preventDefault(); this._over = true; }}
          @dragleave=${e => { if (!e.currentTarget.contains(e.relatedTarget)) this._over = false; }}
          @drop=${e => { e.preventDefault(); this._over = false; this._onFiles(e.dataTransfer.files); }}>
          <span class="pijl">${unsafeSVG(lucideIcon(ArrowUp, 16))}</span>
          <b>${this._busy ? 'Bestand lezen…' : 'Sleep een tekening hierheen'}</b>
          <span class="klein">of <span class="link">kies een bestand</span></span>
          <input type="file" multiple hidden
            accept=".csv,.txt,.tsv,.xlsx,.xlsm,.xls,.dxf,.dwg,.geojson,.json,.kml,.kmz,.zip,.shp,.dbf,.prj,.shx,.pdf,.png,.jpg,.jpeg,.webp"
            @change=${e => this._onFiles(e.target.files)}>
        </label>
        <div class="sectielabel">Wat er daarna gebeurt</div>
        <div class="routes">
          <div class="route">
            <span class="pil blauw">Met coördinaten</span>
            <b>Staat direct goed</b>
            <span>CAD- en GIS-bestanden hebben een eigen plek. We leggen ze meteen op de juiste positie.</span>
            <code>.dwg .dxf .geojson .kml .kmz .zip</code>
          </div>
          <div class="route">
            <span class="pil oranje">Zonder coördinaten</span>
            <b>Jij lijnt hem uit</b>
            <span>Een PDF of afbeelding zet je vast met 3 paspunten: een punt op de tekening en dezelfde plek op de kaart.</span>
            <code>.pdf .png .jpg .webp .tif</code>
          </div>
        </div>
      </div>
      <div class="foot pickfoot">
        <span class="klein">${this.plannerAvailable ? html`Boorpunten of deellocaties overnemen? Gebruik
          <button class="link" @click=${() => { this._close(); this.dispatchEvent(new CustomEvent('open-monsterplan-import', { bubbles: true, composed: true })); }}>Importeren</button>.` : ''}</span>
        <button class="btn plain" @click=${() => this._close()}>Annuleren</button>
      </div>`;
  }

  _renderTable() {
    const sheet = this._sheets[this._sheetIdx];
    const header = this._headerRow >= 0 ? (sheet?.rows[this._headerRow] || []) : [];
    const width = Math.max(0, ...(sheet?.rows || []).slice(0, 50).map(r => r.length));
    const cols = Array.from({ length: width }, (_, i) => ({ i, label: String(header[i] ?? '').trim() || `Kolom ${String.fromCharCode(65 + (i % 26))}` }));
    const colSelect = (key, label, optional) => html`
      <label class="fld"><span>${label}</span>
        <select @change=${e => { this._mapping = { ...this._mapping, [key]: Number(e.target.value) }; }}>
          ${optional ? html`<option value="-1" ?selected=${this._mapping[key] < 0}>— geen —</option>` : ''}
          ${cols.map(c => html`<option value=${c.i} ?selected=${this._mapping[key] === c.i}>${c.label}</option>`)}
        </select>
      </label>`;
    const points = this._previewPoints();
    return html`
      <div class="split">
        <div class="side">
          ${this._sheets.length > 1 ? html`
            <label class="fld"><span>Werkblad</span>
              <select @change=${e => this._selectSheet(Number(e.target.value))}>
                ${this._sheets.map((s, i) => html`<option value=${i} ?selected=${i === this._sheetIdx}>${s.name}</option>`)}
              </select></label>` : ''}
          <label class="fld"><span>Kopregel <em>(regel ${this._headerRow + 1})</em></span>
            <input type="number" min="0" .value=${String(this._headerRow + 1)}
              @change=${e => { this._headerRow = Math.max(0, Number(e.target.value) || 0) - 1; }}>
            <small>0 = geen kopregel</small></label>
          <div class="grid2">
            ${colSelect('name', 'Naam', true)}
            ${colSelect('depth', 'Diepte', true)}
            ${colSelect('x', 'X / Oost / Lon')}
            ${colSelect('y', 'Y / Noord / Lat')}
            ${colSelect('kind', 'Soort', true)}
            ${colSelect('note', 'Opmerking', true)}
          </div>
          ${this._systemPicker()}
          ${this._modePicker(points)}
          ${this._pointList(points)}
        </div>
        <div class="mapcol">${this._mapToolbar()}<div id="preview"></div></div>
      </div>`;
  }

  _renderVector() {
    const points = this._previewPoints();
    const layers = this._dxfFlat ? dxfLayerSummary(this._dxfFlat) : [];
    return html`
      <div class="split">
        <div class="side">
          ${this._systemPicker()}
          ${this._dxf ? html`
            <label class="fld"><span>Eenheid van de tekening</span>
              <select @change=${e => { this._dxfScale = Number(e.target.value); }}>
                <option value="1" ?selected=${this._dxfScale === 1}>meters</option>
                <option value="0.01" ?selected=${this._dxfScale === 0.01}>centimeters</option>
                <option value="0.001" ?selected=${this._dxfScale === 0.001}>millimeters</option>
              </select></label>` : ''}
          <label class="fld"><span>Boorpunten in deze tekening</span>
            <select @change=${e => { this._candIdx = Number(e.target.value); }}>
              <option value="-1" ?selected=${this._candIdx < 0}>— geen, alleen de tekening —</option>
              ${this._candidates.map((c, i) => html`<option value=${i} ?selected=${i === this._candIdx}>${c.source} · ${c.count} st${c.named ? `, ${c.named} met naam` : ''}</option>`)}
            </select>
            <small>Een boorplan uit CAD tekent boorpunten meestal als blok met een nummer. Kies de groep die op de kaart op de goede plekken staat.</small></label>
          ${this._candIdx >= 0 ? html`
            <label class="fld"><span>Soort</span>
              <select @change=${e => { this._defaultKind = e.target.value || null; }}>
                <option value="" ?selected=${!this._defaultKind}>uit de naam afleiden (Pb01 = peilbuis)</option>
                ${PLANNED_POINT_KINDS.map(k => html`<option value=${k.id} ?selected=${this._defaultKind === k.id}>${k.label}</option>`)}
              </select></label>` : ''}
          <label class="check"><input type="checkbox" .checked=${this._saveDrawing} @change=${e => { this._saveDrawing = e.target.checked; }}>
            Tekening als achtergrond op de kaart bewaren</label>
          ${this._saveDrawing ? html`
            <label class="fld"><span>Naam van de tekening</span>
              <input .value=${this._layerName} @input=${e => { this._layerName = e.target.value; }}></label>
            ${layers.length ? html`<small class="muted">${layers.length} CAD-lagen, ${this._dxfFlat.lines.length} lijnen</small>` : ''}` : ''}
          ${this._candIdx >= 0 ? html`${this._modePicker(points)}${this._pointList(points)}` : ''}
        </div>
        <div class="mapcol">${this._mapToolbar()}<div id="preview"></div></div>
      </div>`;
  }

  _renderImage() {
    const t = this._transform();
    const res = t ? residuals(t, this._cps) : [];
    const sysDef = COORDINATE_SYSTEMS[this._georefSystem];
    return html`
      <div class="georef">
        <div class="steps">
          ${this._pending
            ? html`<b>Punt ${this._cps.length + 1}:</b> klik nu hetzelfde punt op de kaart rechts, of typ de coördinaat:
                <input class="xy" placeholder=${sysDef.axes[0]} id="cpx"> <input class="xy" placeholder=${sysDef.axes[1]} id="cpy">
                <button class="mini" @click=${() => this._addTypedCp(this.renderRoot.querySelector('#cpx').value, this.renderRoot.querySelector('#cpy').value)}>Toevoegen</button>
                <button class="mini plain" @click=${() => { this._pending = null; }}>Annuleren</button>`
            : html`${unsafeSVG(lucideIcon(Crosshair, 15))} Klik op de <b>tekening</b> een herkenbaar punt (hoek van een gebouw, perceelgrens, RD-kruis), daarna hetzelfde punt op de <b>kaart</b>. ${this._cps.length < 2 ? html`Nog ${2 - this._cps.length} nodig.` : html`Drie of meer maakt het nauwkeuriger.`}`}
        </div>
        <div class="twin">
          <div class="pane"><div class="pane-label">Tekening</div><div id="imgview"></div></div>
          <div class="pane"><div class="pane-label">Kaart</div>${this._mapToolbar()}<div id="preview"></div></div>
        </div>
        <div class="cpbar">
          ${this._cps.map((c, k) => html`
            <span class="cp">${k + 1}: ${Math.round(c.X)}, ${Math.round(c.Y)}
              ${t && this._cps.length >= 3 ? html`<em class=${res[k] > 2 ? 'bad' : ''}>± ${res[k].toFixed(1)} m</em>` : ''}
              <button class="icon" title="Paspunt weghalen" @click=${() => { this._cps = this._cps.filter((_, i) => i !== k); }}>${unsafeSVG(lucideIcon(Trash2, 12))}</button>
            </span>`)}
          <label class="op">Doorzichtigheid
            <input type="range" min="0.2" max="1" step="0.05" .value=${String(this._opacity)} @input=${e => { this._opacity = Number(e.target.value); }}></label>
          <label class="op">Naam <input .value=${this._layerName} @input=${e => { this._layerName = e.target.value; }}></label>
        </div>
      </div>`;
  }

  _mapToolbar() {
    return html`<div class="seg">
      <button class=${this._base === 'map' ? 'on' : ''} @click=${() => this._setBase('map')}>Kaart</button>
      <button class=${this._base === 'aerial' ? 'on' : ''} @click=${() => this._setBase('aerial')}>Luchtfoto</button>
    </div>`;
  }

  render() {
    if (!this.open) return nothing;
    const step = this._step;
    const canSave = !this._busy && (
      step === 'image' ? this._cps.length >= 2
        : step === 'table' ? this._previewPoints().some(p => !p.error)
          : step === 'vector' ? (this._saveDrawing || this._candIdx >= 0) : false);
    const titles = {
      pick: 'Tekening over de kaart leggen',
      table: 'Meetstaat inlezen',
      vector: 'Tekening inlezen',
      image: this._editLayer ? 'Tekening opnieuw uitlijnen' : 'Tekening uitlijnen',
    };
    return html`
      <div class="back" @click=${e => { if (e.target === e.currentTarget && !this._busy) this._close(); }}>
        <div class="modal ${step === 'pick' ? 'small' : ''}">
          <div class="head">
            <div><h3>${titles[step]}</h3>${step === 'pick'
              ? html`<div class="sub">Een boorplan of situatietekening als achtergrond. Je tekent er daarna zelf op; er worden geen punten overgenomen.</div>`
              : this._files[0] ? html`<div class="sub">${this._files.map(f => f.name).join(', ')}</div>` : ''}</div>
            <button class="x" @click=${() => this._close()}>${unsafeSVG(lucideIcon(X, 18))}</button>
          </div>
          ${this._error ? html`<div class="err">${this._error}</div>` : ''}
          ${step === 'pick' ? this._renderPick() : step === 'table' ? this._renderTable() : step === 'vector' ? this._renderVector() : this._renderImage()}
          ${step !== 'pick' ? html`
            <div class="foot">
              ${this._editLayer
                ? html`<button class="btn plain" ?disabled=${this._busy} @click=${() => this._close()}>Annuleren</button>`
                : html`<button class="btn plain" ?disabled=${this._busy} @click=${() => this._resetAll()}>Ander bestand</button>`}
              <button class="btn primary" ?disabled=${!canSave} @click=${() => this._save()}>${this._busy ? 'Opslaan…' : this._editLayer ? 'Uitlijning bewaren' : 'Op de kaart zetten'}</button>
            </div>` : ''}
        </div>
      </div>`;
  }

  static styles = css`
    ${unsafeCSS(leafletCss)}
    :host { display: contents; }
    .back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 3vh 1rem; }
    .modal { width: min(1180px, 100%); height: min(860px, 94vh); background: var(--mv-surface); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; }
    .modal.small { width: min(460px, 100%); height: auto; border-radius: 16px; }
    .modal.small .head { padding: 20px 20px 4px; }
    .modal.small .head .sub { font-size: .8rem; line-height: 1.45; color: var(--mv-text-sub); margin-top: 4px; }
    .modal.small .x { width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--mv-border); background: var(--mv-surface-alt, #f8fafc);
      display: inline-flex; align-items: center; justify-content: center; }
    .body.pick { padding: 12px 20px 4px; display: flex; flex-direction: column; gap: 14px; }
    .body.pick .drop { gap: 4px; padding: 22px 16px; border-width: 1.5px; border-radius: 10px; }
    .body.pick .drop.over { border-color: var(--mv-primary); background: var(--mv-primary-tint, #e9f5e6); }
    .pijl { width: 28px; height: 28px; border-radius: 50%; background: var(--mv-primary-tint, #e9f5e6); color: var(--mv-primary);
      display: inline-flex; align-items: center; justify-content: center; margin-bottom: 6px; }
    .body.pick .drop b { font-size: .84rem; }
    .klein { font-size: .78rem; color: var(--mv-text-sub); }
    .link { border: none; background: none; padding: 0; font: inherit; color: var(--mv-primary); font-weight: 600; cursor: pointer; }
    .link:hover { text-decoration: underline; }
    .sectielabel { font-size: .64rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--mv-text-muted); margin-bottom: -6px; }
    .routes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .route { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; padding: 12px; border: 1px solid var(--mv-border); border-radius: 10px; }
    .route b { font-size: .82rem; color: var(--mv-text); margin-top: 2px; }
    .route > span:not(.pil) { font-size: .74rem; line-height: 1.45; color: var(--mv-text-sub); }
    .route code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .66rem; color: var(--mv-text-muted); margin-top: 2px; }
    .pil { font-size: .64rem; font-weight: 700; border-radius: 999px; padding: 2px 8px; }
    .pil.blauw { background: #e8eefc; color: #3b5bdb; }
    .pil.oranje { background: #fdf0e6; color: #c2410c; }
    .foot.pickfoot { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 20px; margin-top: 10px; border-top: 1px solid var(--mv-border); }
    .head { display: flex; align-items: flex-start; gap: 12px; padding: 22px 26px 12px; }
    .head h3 { margin: 0; font-size: 1rem; font-weight: 800; color: var(--mv-text); }
    .head .sub { font-size: .8rem; color: var(--mv-text-muted); margin-top: 2px; }
    .x { margin-left: auto; display: inline-flex; background: none; border: none; cursor: pointer; color: var(--mv-text-sub); padding: 4px; }
    .err { margin: 0 26px 10px; padding: 8px 11px; border-radius: 7px; font-size: .82rem;
      color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }
    .body { padding: 6px 26px 26px; display: flex; flex-direction: column; gap: 16px; }
    .drop { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 34px 20px; border: 2px dashed var(--mv-border);
      border-radius: 12px; color: var(--mv-text-muted); cursor: pointer; text-align: center; font-size: .88rem; }
    .drop:hover { border-color: var(--mv-primary); color: var(--mv-primary); }
    .drop b { color: var(--mv-text); font-weight: 600; }
    .kinds { display: flex; flex-direction: column; gap: 8px; font-size: .82rem; color: var(--mv-text-sub); }
    .kinds div { display: flex; gap: 9px; align-items: flex-start; }
    .kinds b { color: var(--mv-text); margin-right: 4px; }
    .split { flex: 1; min-height: 0; display: flex; border-top: 1px solid var(--mv-border); }
    .side { width: 340px; flex-shrink: 0; overflow-y: auto; padding: 14px 18px; display: flex; flex-direction: column; gap: 12px;
      border-right: 1px solid var(--mv-border); box-sizing: border-box; }
    .mapcol { flex: 1; position: relative; min-width: 0; }
    #preview { position: absolute; inset: 0; background: #e9e7e0; }
    .seg { position: absolute; top: 10px; right: 10px; z-index: 500; display: flex; background: var(--mv-surface);
      border: 1px solid var(--mv-border); border-radius: 7px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.12); }
    .seg button { border: none; background: none; padding: 6px 12px; font: 600 .76rem inherit; font-family: inherit; color: var(--mv-text-muted); cursor: pointer; }
    .seg button.on { background: var(--mv-primary); color: #fff; }
    .fld { display: flex; flex-direction: column; gap: 5px; }
    .fld span { font-size: .78rem; font-weight: 600; color: var(--mv-text-sub); }
    .fld em { font-style: normal; font-weight: 500; color: var(--mv-text-muted); }
    .fld em.warn { color: var(--mv-danger, #B91C1C); }
    .fld small, small.muted { font-size: .72rem; color: var(--mv-text-muted); line-height: 1.4; }
    .fld input, .fld select, .op input:not([type=range]) { padding: 7px 9px; border: 1px solid var(--mv-border); border-radius: 7px;
      font: inherit; font-size: .84rem; background: var(--mv-surface); color: var(--mv-text); min-width: 0; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .check { display: flex; gap: 8px; align-items: center; font-size: .82rem; color: var(--mv-text); cursor: pointer; }
    .check em { font-style: normal; color: var(--mv-text-muted); }
    .sum { font-size: .82rem; color: var(--mv-text); }
    .bad { color: var(--mv-danger, #B91C1C); }
    .plist { border: 1px solid var(--mv-border); border-radius: 8px; max-height: 260px; overflow-y: auto; }
    .prow { display: grid; grid-template-columns: 1fr 64px 58px 74px; gap: 6px; padding: 5px 9px; font-size: .78rem;
      border-bottom: 1px solid var(--mv-border); color: var(--mv-text); }
    .prow:last-child { border-bottom: none; }
    .prow.err-row { color: var(--mv-danger, #B91C1C); }
    .pname { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .pkind, .pdepth, .pstate { color: var(--mv-text-muted); }
    .georef { flex: 1; min-height: 0; display: flex; flex-direction: column; border-top: 1px solid var(--mv-border); }
    .steps { padding: 10px 18px; font-size: .84rem; color: var(--mv-text); display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
      background: var(--mv-primary-tint, rgba(46,158,91,.06)); }
    .xy { width: 110px; padding: 5px 7px; border: 1px solid var(--mv-border); border-radius: 6px; font: inherit; font-size: .82rem; }
    .twin { flex: 1; min-height: 0; display: flex; }
    .pane { flex: 1; position: relative; min-width: 0; border-right: 1px solid var(--mv-border); }
    .pane:last-child { border-right: none; }
    .pane-label { position: absolute; top: 10px; left: 50px; z-index: 500; background: var(--mv-surface); border: 1px solid var(--mv-border);
      border-radius: 6px; padding: 3px 8px; font-size: .72rem; font-weight: 700; color: var(--mv-text-sub); }
    #imgview { position: absolute; inset: 0; background: #f4f4f2; cursor: crosshair; }
    .cpbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 9px 18px; border-top: 1px solid var(--mv-border); font-size: .78rem; }
    .cp { display: inline-flex; align-items: center; gap: 5px; padding: 3px 6px 3px 9px; border: 1px solid var(--mv-border); border-radius: 999px; }
    .cp em { font-style: normal; color: var(--mv-text-muted); }
    .cp em.bad { color: var(--mv-danger, #B91C1C); }
    .op { display: inline-flex; align-items: center; gap: 6px; color: var(--mv-text-sub); margin-left: auto; }
    .op + .op { margin-left: 0; }
    .icon { border: none; background: none; cursor: pointer; color: var(--mv-text-muted); display: inline-flex; padding: 2px; }
    .mini { border: 1px solid var(--mv-primary); background: var(--mv-primary); color: #fff; border-radius: 6px; padding: 4px 10px; font: 600 .76rem inherit; font-family: inherit; cursor: pointer; }
    .mini.plain { background: none; color: var(--mv-text-sub); border-color: var(--mv-border); }
    .foot { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 22px; border-top: 1px solid var(--mv-border); }
    .btn { border-radius: 8px; padding: 8px 16px; font: 700 .84rem inherit; font-family: inherit; cursor: pointer; border: 1px solid var(--mv-border); }
    .btn.plain { background: var(--mv-surface); color: var(--mv-text); }
    .btn.primary { background: var(--mv-primary); border-color: var(--mv-primary); color: #fff; }
    .btn:disabled { opacity: .5; cursor: default; }
    .leaflet-container { font: inherit; }
  `;
}


customElements.define('map-import-dialog', MapImportDialog);
